import { Injectable } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import ODataContext from 'devextreme/data/odata/context';
import { DataItem, DataItems, LatestDate, Report, Reports, ReverseDimensionMap } from '../interfaces';
// import { EdmLiteral } from 'devextreme/data/odata/utils';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';

/**
 * DataService class provides typesafe access methods for the app's entities
 * Providing Observables that can be subscribe to get the result value. By using
 * that methods provided, the database operations running parallel making the
 * application more responcive.
 */
@Injectable({
  providedIn: 'root',
})
export class DataService {
  get latestDate(): LatestDate {
    return this._latest_date;
  }

  get reportId(): string {
    return this._report_id;
  }

  set reportId(report_id: string) {
    this._report_id = report_id;
  }

  get currentReport(): Report {
    return this._currentReport;
  }

  get odataCtx(): ODataContext {
    return this._odataCtx;
  }

  private _report_id = 'AnnualCoalProducts';
  private _latest_date: LatestDate = { year: 2022, month: 12, day: 31 };
  private _currentReport: any;
  private _odataCtx: any;

  // dataSource$: Observable<any> = new Observable<any>();
  // subject$: Subject<any> = new Subject<any>();

  constructor(private http: HttpClient) {
    this._odataCtx = new ODataContext({
      url: 'odata/v4/public',
      version: 4,
      entities: {
        // Controll entities
        Auditlog: { key: 'id', keyType: 'String' },
        BatchAudit: { key: 'id', keyType: 'Int64' },
        Dimension: { key: 'id', keyType: 'String' },
        Report: { key: 'id', keyType: 'String' },
        // Data entities
        AnnualCoalProducts: { key: 'id', keyType: 'Int32' }, // 01
        SzallitorendszerMerleg: { key: 'id', keyType: 'Int32' }, // 02
        FoldgaztarolasAdatok: { key: 'id', keyType: 'Int32' }, // 03
        AnnualOilTrade: { key: 'id', keyType: 'Int32' }, // 04
        MonthlyGasBalance: { key: 'id', keyType: 'Int32' }, // 05
        MonthlyPrimaryOilProducts: { key: 'id', keyType: 'Int32' }, // 06
        MonthlyCoalProducts: { key: 'id', keyType: 'Int32' }, // 07
        MonthlyElectricityBalance: { key: 'id', keyType: 'Int32' }, // 08
        MonthlyPrimaryEnergyBalance: { key: 'id', keyType: 'Int32' }, // 09
        AnnualPrimaryOilProducts: { key: 'id', keyType: 'Int32' }, // 10
        AnnualGasBalance: { key: 'id', keyType: 'Int32' }, // 11
        AnnualGrossElectricity: { key: 'id', keyType: 'Int32' }, // 12
        AnnualElectricityBalance: { key: 'id', keyType: 'Int32' }, // 13
        AnnualHeatProduction: { key: 'id', keyType: 'Int32' }, // 14
        ShareofRenewable: { key: 'id', keyType: 'Int32' }, // 15
        PrimaryRenewable: { key: 'id', keyType: 'Int32' }, // 16
        AnnualHouseholdsConsumption: { key: 'id', keyType: 'Int32' }, // 17
        AnnualIndustryConsumption: { key: 'id', keyType: 'Int32' }, // 18
      },
      beforeSend: (e) => {
        e.headers = {
          Accept: 'application/json;odata.metadata=none',
        };
      },
    });
  }

  /**
   * getOneItem retrives one DataItem row for the specified report_id
   * params:
   *  report_id: the id of the report which the data values bilogs to
   *  id: id of the DataItems row
   *  options: (optional) object that contains Odata query options (only select & expand  allowed)
   *            for instance: { select: ['id', 'dimension_1', 'date', 'value_1'] }
   * return: one of entity rows, type DataItem
   */
  getOneItem(report_id: string | undefined, id: string | number, options?: object | undefined): Observable<DataItem> {
    if (report_id !== undefined) this._report_id = report_id;
    // TODO if the keyType of entitiy is Int64 the key should be converted
    // let _id = Number.isInteger(id) ? new EdmLiteral(`${id}L`) : id;

    const oneDS$ = from(this._odataCtx[this._report_id].byKey(id, options)) as Observable<DataItem>;
    return oneDS$;
  }

  /**
   * getAllItems retrives all DataItem rows for the specified report_id
   * params:
   *  report_id: the id of the report
   *  options: (optional) object that contains Odata query options (example:
   *    {
   *      select: ['id', 'dimension_1', 'dimension_2', 'dimension_3', 'date', 'value_1', 'value_2'],
   *      filter: [['dimension_1', 'IMPORTS']],
   *      sort: ['dimension_2', { selector: 'date', desc: true }]
   *     }
   * return: array of entity rows, type DataItem
   */
  getAllItems(report_id?: string | undefined, options?: object | undefined): Observable<DataItems> {
    if (report_id !== undefined) this._report_id = report_id;

    const dataDS$ = from(this._odataCtx[this._report_id].load(options)) as Observable<DataItems>;
    return dataDS$;
  }

  /**
   * getCurrentReport retrives one Report entity row that specified by report_id
   * params:
   *  report_id: the id of the report
   *  options: (optional) object that contains Odata query options (only select allowed)
   *            for instance: { select: ['id', 'title_hu'] }
   * return: one of entity rows, type Report
   */
  getCurrentReport(report_id?: string | undefined, options?: object | undefined): Observable<Report> {
    if (report_id !== undefined) this._report_id = report_id;

    const reportDS$ = from(this._odataCtx.Report.byKey(this._report_id, options)) as Observable<Report>;
    reportDS$.subscribe((data: Report) => {
      this._currentReport = data;
    });

    return reportDS$;
  }

  /**
   * getAllReports retrives all DataItem rows for the specified report_id
   * params:
   *  report_id: the id of the report
   *  options: (optional) object that contains Odata query options (example:
   *            for instance: { select: ['id', 'title_hu'] }
   * return: array of Report entity row
   */
  getAllReports(report_id?: string | undefined, options?: object | undefined): Observable<Reports> {
    if (report_id !== undefined) this._report_id = report_id;

    const dataDS$ = from(this._odataCtx.Report.load(options)) as Observable<Reports>;
    return dataDS$;
  }

  /**
   * getDimensions retrives all DataItem rows for the specified report_id
   * params:
   *  report_id: the id of the report
   *  options: (optional) object that contains Odata query options
   *    {
   *      select: ['id', this.label, 'group', 'order'],
   *      filter: [['report_id', reportId]],
   *      sort: ['group', 'order'],
   *      expand: [ parent($select=id,label_hu;$orderby=order) ]
   *    }
   * return: array of Dimensions entity rows
   */
  getDimensions(report_id?: string | undefined, options?: object | undefined): Observable<ReverseDimensionMap> {
    if (report_id !== undefined) this._report_id = report_id;

    const dimensionDS$ = from(this._odataCtx.Dimension.load(options)) as Observable<ReverseDimensionMap>;
    return dimensionDS$;
  }

  /**
   * getLatestDate retrives the LatestDate from DataItem entity for the specified report_id
   * Can be used for instance to set the latest year of the data awailable.
   * params:
   *  report_id: the id of the report
   * return: the latest date in object format (for instance: { year: 2020, month: 06, day: 01 })
   */
  getLatestDate(report_id?: string | undefined): Observable<LatestDate> {
    if (report_id !== undefined) this._report_id = report_id;

    const dataSource = new DataSource({
      store: this._odataCtx[this._report_id],
      select: ['date'],
      sort: { selector: 'date', desc: true },
      map: (item) => {
        const parts = item.date.split('-');
        return {
          year: Number.parseInt(parts.shift(), 10),
          month: Number.parseInt(parts.shift(), 10),
          day: Number.parseInt(parts.pop(), 10),
        };
      },
      pageSize: 1,
    });

    const dateDS$ = from(dataSource.load());
    dateDS$.subscribe((data: LatestDate[]) => {
      this._latest_date = data.shift() as LatestDate;
      console.dir('Created the latest date: ', this._latest_date);
    });

    return dateDS$;
  }
}
